import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, Plus, TrendingUp, TrendingDown, BarChart3, AlertTriangle, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function BudgetPage() {
  const [tab, setTab] = useState('compare');
  const [budgets, setBudgets] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', department: '', period: new Date().toISOString().slice(0, 7), categories: [{ name: '', amount: 0 }] });

  const fetchData = async () => {
    try {
      const [budgetRes, compRes] = await Promise.all([
        api.get('/budgets', { params: { period } }),
        api.get('/budgets/comparison', { params: { period } }),
      ]);
      setBudgets(budgetRes.data);
      setComparison(compRes.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [period]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try { await api.post('/budgets', form); toast.success('Budget created'); setShowCreate(false); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/budgets/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const addCat = () => setForm(f => ({ ...f, categories: [...f.categories, { name: '', amount: 0 }] }));
  const updateCat = (i, field, val) => { const cs = [...form.categories]; cs[i] = { ...cs[i], [field]: val }; setForm(f => ({ ...f, categories: cs })); };

  const s = comparison?.summary || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="budget-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <BarChart3 size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight dark:text-white">Budget vs Actual</h1>
              <p className="text-sm text-muted-foreground">Track spending against budgets</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" data-testid="budget-period" />
            <Button onClick={() => setShowCreate(true)} data-testid="create-budget-btn"><Plus size={14} className="mr-1.5" />New Budget</Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="budget-summary">
          <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Budgeted</div><div className="text-xl font-bold dark:text-white" data-testid="total-budgeted">{s.total_budgeted?.toLocaleString() || 0}</div></CardContent></Card>
          <Card className="border-l-4 border-l-amber-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Actual Spent</div><div className="text-xl font-bold dark:text-white" data-testid="total-actual">{s.total_actual?.toLocaleString() || 0}</div></CardContent></Card>
          <Card className={`border-l-4 ${(s.total_variance || 0) >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Variance</div><div className={`text-xl font-bold ${(s.total_variance || 0) >= 0 ? 'text-success' : 'text-error'}`} data-testid="total-variance">{s.total_variance >= 0 ? '+' : ''}{s.total_variance?.toLocaleString() || 0}</div></CardContent></Card>
          <Card className="border-l-4 border-l-violet-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Utilization</div><div className="text-xl font-bold dark:text-white" data-testid="utilization">{s.overall_utilization || 0}%</div></CardContent></Card>
        </div>

        {/* Comparisons */}
        <Card data-testid="comparisons-card">
          <CardHeader><CardTitle className="text-sm">Department Budgets</CardTitle></CardHeader>
          <CardContent>
            {(comparison?.comparisons || []).length === 0 ? (
              <p className="text-center py-8 text-muted-foreground text-sm">No budgets for this period. Create one above.</p>
            ) : (
              <div className="space-y-3">
                {comparison.comparisons.map(c => (
                  <div key={c.budget_id} className="p-4 border rounded-xl dark:border-stone-700" data-testid={`budget-compare-${c.budget_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold dark:text-white">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground">{c.department}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={c.status === 'under' ? 'default' : 'destructive'} className="text-[10px]">
                          {c.status === 'under' ? <TrendingDown size={10} className="mr-0.5" /> : <AlertTriangle size={10} className="mr-0.5" />}
                          {c.utilization}%
                        </Badge>
                        <Button size="sm" variant="ghost" className="text-red-400 h-6 w-6 p-0" onClick={() => handleDelete(c.budget_id)}><Trash2 size={12} /></Button>
                      </div>
                    </div>
                    <div className="h-3 bg-muted dark:bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${c.utilization > 100 ? 'bg-error' : c.utilization > 80 ? 'bg-amber-400' : 'bg-success'}`} style={{ width: `${Math.min(c.utilization, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                      <span>Budgeted: {c.budgeted?.toLocaleString()}</span>
                      <span>Actual: {c.actual?.toLocaleString()}</span>
                      <span>Variance: {c.variance >= 0 ? '+' : ''}{c.variance?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent data-testid="create-budget-dialog">
            <DialogHeader><DialogTitle>Create Budget</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="budget-name-input" className="mt-1" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))} className="mt-1" /></div>
              <div><Label>Period</Label><Input type="month" value={form.period} onChange={(e) => setForm(f => ({ ...f, period: e.target.value }))} className="mt-1" /></div>
              <Label>Categories</Label>
              {form.categories.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={c.name} onChange={(e) => updateCat(i, 'name', e.target.value)} placeholder="Category" className="flex-1" />
                  <Input type="number" value={c.amount} onChange={(e) => updateCat(i, 'amount', parseFloat(e.target.value) || 0)} className="w-32" placeholder="Amount" />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addCat} className="w-full border-dashed"><Plus size={14} className="mr-1" />Add Category</Button>
              <Button onClick={handleCreate} className="w-full" data-testid="create-budget-submit">Create Budget</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
